
import { RICHTEXT_EDITOR_CONFIG } from './shared/richtext-editor-config';
import { DestroyRef, Directive, inject, OnInit } from '@angular/core';
import { PolicyService } from '../../services/roles/policy.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, AbstractControl } from '@angular/forms';
import { combineLatest, filter } from 'rxjs';
import { ReportFormStore } from '../../services';
import { ReportFormPage } from '../../services/report-form-control.service';

@Directive()
export abstract class ReportForm implements OnInit {
  protected _policy = inject(PolicyService);
  protected _store = inject(ReportFormStore);
  protected _destroy = inject(DestroyRef);

  public readonlyFields: string[] = [];

  public abstract form: FormGroup;
  public abstract step: ReportFormPage;
  protected canEdit = false;

  protected getStepValidity(): boolean {
    return !!this.form?.valid;
  }

  private _applyCanEditState(canEdit: boolean): void {
    if (!this.form) {
      return;
    }

    if (canEdit) {
      const controls = this.form.controls;
      Object.keys(controls).forEach((key) => {
        if (this.readonlyFields.includes(key)) {
          controls[key].disable({ emitEvent: false });
        } else {
          controls[key].enable({ emitEvent: false });
        }
      });
    } else {
      this.form.disable({ emitEvent: false });
    }

    this.form.updateValueAndValidity({ emitEvent: false });

    // Notify child components (e.g. rich-text editors) of updated edit state.
    this.onPolicyOrFormStateChanged();
  }

  /**
   * Hook for child components to react to policy or form state changes.
   * Override in child component if needed.
   */
  protected onPolicyOrFormStateChanged(): void { }

  ngOnInit(): void {
    // Subscribe to canEditForPage$ using THIS tab's step so that enable/disable logic
    // is always computed for THIS tab, regardless of which tab is currently active.
    // canEdit$ depends on page$ (the active tab) which would incorrectly apply another
    // tab's policy to this form, causing spurious invalid-form errors on lifecycle change.
    this._policy.canEditForPage$(this.step)
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe((canEdit) => {
        this.canEdit = canEdit;

        // First canEdit emission can happen before child initializeForm().
        // Defer once to microtask so form init in child ngOnInit has a chance to complete.
        if (!this.form) {
          Promise.resolve().then(() => this._applyCanEditState(canEdit));
          return;
        }

        this._applyCanEditState(canEdit);
      });

    // Handle page changes: update page validity and call lifecycle hook only when on current page
    this._store.page$
      .pipe(
        filter(page => page === this.step),
        takeUntilDestroyed(this._destroy)
      )
      .subscribe(() => {
        this._store.setPageValidity({ 
          page: this.step, 
          isStepValid: this.canEdit ? this.getStepValidity() : false 
        });
        this.onPolicyOrFormStateChanged();
      });
  }
  public isFieldEditable(fieldName: string): boolean {
    const control = this.form?.get(fieldName);
    return !!(this.canEdit && control && !control.disabled);
  }
}
